import { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Modal,
  Row,
  Col,
  Divider,
  Tooltip,
  Select,
  Input,
  Skeleton,
} from 'antd';
import moment from 'moment';

import {
  useAccountPayableAndInstallmentUpdate,
  useAccountPayableReturnListInstallments,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  AccountPayableReleaseFormValues,
  AccountPayableReleaseUpdateRequest,
  AccountPayableSituationColor,
  Creditor,
  InstallmentSituation,
  InstallmentSituationType,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useEmployeeDetailReduced } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';

import ContentWrapper from '../ContentWrapper';
import FloatingFormItem from '../FloatingFormItem';
import CompaniesInfiniteSelectField from '../CompaniesInfiniteSelectField';
import PlanAccountsSelect from '../PlanAccountsSelect';
import CostCenterInfiniteSelectField from '../CostCenterInfiniteSelectField';
import DocumentTypesInfiniteSelectField from '../DocumentTypesInfiniteSelectField';
import InputDate from '../InputDate';
import InputDecimal from '../InputDecimal';
import InputSearch from '../InputSearch';
import AccountPayableObservationList from '@/components/Registry/BillToPay/ReleaseForm/AccountPayableObservationList';
import StatusTag from '@/components/_common/StatusTag';

import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import useDefaultMinLengthNegativeFieldError from '@/hooks/useDefaultMinLengthNegativeFieldError';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';

import EmployeesSelectedModal from '../EmployeesSelectedModal';
import SuppliersSelectedModal from '../SuppliersSelectedModal';
import { Employee } from '@/core/domain/schedules/Schedules.types';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { DateTimeFormat } from '@/utils/moment';
import BankAccountInfiniteSelectField from '../BankAccountInfiniteSelectField';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  open: boolean;
  onCancel: () => void;
  accountPayableId?: number;
}

const BillToPayEditModal = ({ open, onCancel, accountPayableId }: Props) => {
  const [form] = Form.useForm<AccountPayableReleaseFormValues>();
  const [currentInstallmentIndex, setCurrentInstallmentIndex] = useState(0);

  const accountPayable = useAccountPayableReturnListInstallments(
    Number(accountPayableId),
  );
  const employee = useEmployeeDetailReduced(accountPayable.data?.employeeId);
  const supplier = useSupplier(accountPayable.data?.supplierId);
  const companySettings = useCompanySettings(accountPayable.data?.companyId);
  const accountPayableAndInstallmentUpdate =
    useAccountPayableAndInstallmentUpdate();

  const accountPayableData = accountPayable.data;
  const installmentPayables = accountPayableData?.installmentPayables || [];
  const installmentData = installmentPayables[currentInstallmentIndex];

  const supplierId = Form.useWatch('supplierId', form);
  const installmentValue = Form.useWatch('installmentValue', form) || 0;
  const discountIncrease = Form.useWatch('discountIncrease', form) || 0;
  const creditor = Form.useWatch('creditor', form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLengthNegativeFieldError =
    useDefaultMinLengthNegativeFieldError();

  const [modalEmployeeVisible, setModalEmployeeVisible] = useState(false);
  const [modalSupplierVisible, setModalSupplierVisible] = useState(false);

  const disabledBySituation =
    installmentData &&
    (installmentData.situation === InstallmentSituation.settled ||
      installmentData.situation === InstallmentSituation.canceled);

  const hasInstallmentsSettled = useMemo(
    () =>
      installmentPayables.some(
        (installment) => installment.situation !== InstallmentSituation.open,
      ),
    [installmentPayables],
  );

  const statusColor = installmentData
    ? AccountPayableSituationColor[installmentData.situation]
    : 'default';

  const statusLabel = installmentData
    ? InstallmentSituationType[installmentData.situation]
    : '';

  useEffect(() => {
    if (accountPayableId && accountPayable.data) {
      const initialIndex = accountPayable.data.installmentPayables.findIndex(
        (item) => accountPayableId === item.id,
      );
      setCurrentInstallmentIndex(initialIndex >= 0 ? initialIndex : 0);
    }
  }, [accountPayableId, accountPayable.data]);

  useEffect(() => {
    if (accountPayable.data && open) {
      const { installmentPayables, ...restAccountPayable } =
        accountPayable.data;
      const installmentPayable = installmentPayables[currentInstallmentIndex];

      form.setFieldsValue({
        ...restAccountPayable,
        ...installmentPayable,
        dueDate: installmentPayable?.dueDate
          ? moment(installmentPayable.dueDate)
          : undefined,
        emissionDate: installmentPayable?.emissionDate
          ? moment(installmentPayable.emissionDate)
          : undefined,
        payDate: installmentPayable?.payDate
          ? moment(installmentPayable.payDate)
          : undefined,
        installmentValue: installmentPayable?.installmentValue || 0,
        discountIncrease: installmentPayable?.discountIncrease || 0,
        credit: installmentPayable?.credit || 0,
        totalAmount:
          (installmentPayable?.installmentValue || 0) +
          (installmentPayable?.discountIncrease || 0) -
          (installmentPayable?.credit || 0),
      });
    }
  }, [accountPayable.data, currentInstallmentIndex, open, form]);

  useEffect(() => {
    if (employee.data) {
      form.setFieldsValue({ employee: employee.data.name });
    } else if (supplier.data) {
      form.setFieldsValue({ supplier: supplier.data.name });
    }
  }, [employee.data, supplier.data, form]);

  const handleFormValuesChange = (
    _valuesChanged: AccountPayableReleaseFormValues,
    fieldValues: AccountPayableReleaseFormValues,
  ) => {
    form.setFieldsValue({
      totalAmount:
        (fieldValues.installmentValue ?? 0) +
        (fieldValues.discountIncrease ?? 0) -
        (fieldValues.credit ?? 0),
    });
  };

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: supplier?.name,
      supplierId: supplier?.id,
    });
    setModalSupplierVisible(false);
  };

  const handleEmployeeSelect = (employee?: Employee) => {
    form.setFieldsValue({
      employee: employee?.name,
      employeeId: employee?.id,
    });
    setModalEmployeeVisible(false);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const installmentPayable = installmentPayables[currentInstallmentIndex];

      const obj: AccountPayableReleaseUpdateRequest = {
        ...accountPayableData,
        ...values,
        accountPayableId: Number(accountPayableData?.id),
        installmentPayableId: Number(installmentPayable?.id),
        emissionDate: values.emissionDate?.format(DateTimeFormat),
        dueDate: values.dueDate?.format(DateTimeFormat),
        currentSituation: installmentPayable?.situation,
      };

      await accountPayableAndInstallmentUpdate.mutateAsync(obj);
      handleCancel();
    } catch (error) {
      console.error('Error updating account payable:', error);
    }
  };

  const renderCreditorField = () => {
    if (creditor === Creditor.employee) {
      return (
        <Col span={24} md={18} xl={10}>
          <Form.Item hidden name="employeeId" />
          <FloatingFormItem
            name="employee"
            label="Funcionário"
            rules={[defaultEmptyFieldError]}
          >
            <InputSearch
              inputProps={{ disabled: true }}
              buttonProps={{
                onClick: () => setModalEmployeeVisible(true),
                disabled: hasInstallmentsSettled || disabledBySituation,
              }}
            />
          </FloatingFormItem>
        </Col>
      );
    }

    return (
      <Col span={24} md={18} xl={10}>
        <Form.Item hidden name="supplierId" />
        <FloatingFormItem
          name="supplier"
          label="Fornecedor"
          rules={[defaultEmptyFieldError]}
        >
          <InputSearch
            inputProps={{ disabled: true }}
            buttonProps={{
              onClick: () => setModalSupplierVisible(true),
              disabled:
                hasInstallmentsSettled ||
                disabledBySituation ||
                accountPayableData?.purchase,
            }}
          />
        </FloatingFormItem>
      </Col>
    );
  };

  const renderFinancialFields = () => (
    <>
      <Col span={24} md={12} lg={6}>
        <FloatingFormItem
          label="(R$) Valor"
          name="installmentValue"
          rules={[
            defaultEmptyFieldError,
            {
              type: 'number',
              min: 0.01,
              message:
                'O valor da parcela não pode ser igual ou menor que zero!',
            },
          ]}
        >
          <InputDecimal min={0} disabled={disabledBySituation} />
        </FloatingFormItem>
      </Col>

      <Col span={24} md={12} lg={6}>
        <FloatingFormItem
          label="(R$) Acresc./Desc."
          name="discountIncrease"
          initialValue={0}
          rules={
            !companySettings.data?.allowNegativeLiquidValueInPayables
              ? [defaultMinLengthNegativeFieldError(installmentValue * -1)]
              : []
          }
        >
          <InputDecimal disabled={disabledBySituation} />
        </FloatingFormItem>
      </Col>

      <Col span={24} md={12} lg={6}>
        <Tooltip
          title={`Crédito disponível: ${formatterPrice(
            supplier.data?.credit || 0,
          )}`}
          trigger="click"
        >
          <FloatingFormItem
            name="credit"
            label="Crédito Fornecedor"
            initialValue={0}
            rules={
              !companySettings.data?.allowNegativeLiquidValueInPayables
                ? [
                    {
                      type: 'number',
                      max: installmentValue + discountIncrease,
                      message: `Valor não pode ser maior que ${formatterPrice(
                        installmentValue + discountIncrease,
                      )}`,
                    },
                  ]
                : []
            }
          >
            <InputSearch
              viewIcon
              valueMax={supplier.data?.credit ?? 0}
              inputDecimal
              buttonProps={{
                onClick: () =>
                  window.open(
                    `/app/registry/suppliers/${supplierId}/supplierCredit`,
                    '_blank',
                  ),
                disabled: !supplierId || disabledBySituation,
              }}
              disabledInputDecimal={disabledBySituation}
            />
          </FloatingFormItem>
        </Tooltip>
      </Col>

      <Col span={24} md={12} lg={6}>
        <FloatingFormItem
          label="(R$) Valor Líquido"
          name="totalAmount"
          initialValue={0}
        >
          <InputDecimal disabled />
        </FloatingFormItem>
      </Col>
    </>
  );

  return (
    <Modal
      title="Conta a pagar"
      open={open}
      width="90%"
      okText="Salvar"
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{
        loading: accountPayableAndInstallmentUpdate.isLoading,
        disabled:
          !hasPermissions([Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT]) ||
          accountPayable.data?.installmentPayables[currentInstallmentIndex]
            ?.situation === InstallmentSituation.settled ||
          accountPayable.data?.installmentPayables[currentInstallmentIndex]
            ?.situation === InstallmentSituation.canceled,
      }}
      confirmLoading={accountPayableAndInstallmentUpdate.isLoading}
    >
      <Skeleton loading={accountPayable.isLoading}>
        <Form
          layout="vertical"
          form={form}
          onValuesChange={handleFormValuesChange}
        >
          <ContentWrapper
            style={{ marginBottom: 20, padding: '1.5rem 2rem 1.25rem' }}
          >
            <Row gutter={[16, 9]}>
              <Col span={24} xl={10}>
                <FloatingFormItem name="companyId" label="Empresa">
                  <CompaniesInfiniteSelectField
                    disabled={hasInstallmentsSettled || disabledBySituation}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={6} xl={4}>
                <FloatingFormItem
                  label="Credor"
                  name="creditor"
                  rules={[defaultEmptyFieldError]}
                  initialValue={Creditor.supplier}
                >
                  <Select
                    allowClear
                    options={[
                      { value: Creditor.supplier, label: 'Fornecedor' },
                      { value: Creditor.employee, label: 'Funcionário' },
                    ]}
                    disabled={
                      hasInstallmentsSettled ||
                      disabledBySituation ||
                      accountPayableData?.purchase
                    }
                  />
                </FloatingFormItem>
              </Col>

              {renderCreditorField()}

              <Col span={24} md={12} xl={12}>
                <FloatingFormItem name="planAccountId" label="Plano de Contas">
                  <PlanAccountsSelect
                    queryFilter={{
                      hierarchy: PlanAccountHierarchy.Analitical,
                      destination: PlanAccountDestination.Expense,
                      active: true,
                    }}
                    allowClear
                    disabled={hasInstallmentsSettled || disabledBySituation}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} xl={12}>
                <FloatingFormItem name="costCenterId" label="Centro de Custo">
                  <CostCenterInfiniteSelectField
                    disabled={hasInstallmentsSettled || disabledBySituation}
                    allowClear
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </ContentWrapper>

          <Row gutter={[16, 9]}>
            <Col span={24}>
              <FloatingFormItem name="situation">
                <StatusTag color={statusColor} label={statusLabel} />
              </FloatingFormItem>
            </Col>
          </Row>

          <Row gutter={[16, 9]}>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Descrição" name="description">
                <Input disabled={disabledBySituation} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Numero do Documento"
                name="documentNumber"
                rules={[defaultMaxLenghtFieldError(255)]}
              >
                <Input disabled={disabledBySituation} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Tipo de Documento"
                name="documentTypeId"
                rules={[defaultEmptyFieldError]}
              >
                <DocumentTypesInfiniteSelectField
                  disabled={disabledBySituation}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Dt. Emissão"
                name="emissionDate"
                rules={[defaultEmptyFieldError]}
              >
                <InputDate disabled={disabledBySituation} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Dt. Vencimento"
                name="dueDate"
                rules={[defaultEmptyFieldError]}
              >
                <InputDate disabled={disabledBySituation} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Dt. de Pagamento" name="payDate">
                <InputDate disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem name="bankAccountId" label="Conta Bancária">
                <BankAccountInfiniteSelectField
                  disabled={disabledBySituation}
                  queryFilter={{ active: true }}
                />
              </FloatingFormItem>
            </Col>
          </Row>

          <Row gutter={[16, 9]}>{renderFinancialFields()}</Row>

          {installmentData?.id && (
            <>
              <Divider />
              <Col span={24}>
                <AccountPayableObservationList
                  installmentPayableId={installmentData.id}
                />
              </Col>
            </>
          )}
        </Form>
      </Skeleton>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={() => setModalEmployeeVisible(false)}
        noSelection
        onEmployeeSelect={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={() => setModalSupplierVisible(false)}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </Modal>
  );
};

export default BillToPayEditModal;
